<template>
  <v-card>
    <v-card-title class="primary white--text">
      <span>{{ tractGroupId ? $t('editTractGroup') : $t('newTractGroup') }}</span>
      <v-spacer/>
      <BaseDialogActions hideRefresh @close="$emit('close')"/>
    </v-card-title>
    <v-card-text>
      <FormWrapper :disabled="!isFormValid" @submit="save" buttonId="save-tract-group">
        <v-container fluid>
        <v-alert
          v-if="missingGroupRequirements.length > 0"
          type="warning"
          outlined
          text
          dense
          data-testid="tract-group-missing-requirements">
          {{ $t('tractGroupMissingCreationRequirements', { fields: requiredFieldMessages(missingGroupRequirements) }) }}
        </v-alert>
        <v-row>
          <v-col cols="12" md="4">
            <LoadableComponent :loading="loading">
              <v-text-field
                v-model="tractGroup.name"
                :label="$t('tractGroupName')"
                :rules="[rules.required]"
                color="black"
                counter
                maxlength="40"
                id="tract-group-name"
                ref="nameField"
              />
            </LoadableComponent>
          </v-col>
          <v-col cols="12" md="2">
            <LoadableComponent :loading="loading">
              <v-text-field
                v-model="tractGroup.code"
                :label="$t('code')"
                color="black"
                counter
                maxlength="10"
              />
            </LoadableComponent>
          </v-col>
          <v-col cols="12" md="6">
            <LoadableComponent :loading="loading">
              <TractTypeSelector
                id="tract-group-type"
                :tractTypeId="tractGroup.tractTypeId"
                @tract-type="onTractTypeChosen"
                :hint="$t('required')"
                :persistentHint="true"
                :propFetchTractTypes="false"
                :filter="obligationTractType"
                selectFirst
              />
            </LoadableComponent>
          </v-col>
        </v-row>
        <v-row class="mt-n3">
          <v-col cols="6">
            <v-card outlined class="fill-height">
              <v-card-text class="pt-0">
                <v-row dense>
                  <v-col cols="12" class="mt-2">
                    <LoadableComponent :loading="loading">
                      <AccountAutocomplete
                        id="tract-group-consulting-forester"
                        :accountId="tractGroup.consultingForesterAccountId"
                        :title="$t('consultingForester')"
                        clearable
                        showCertified
                        userSetting="consultingForesterAccountAutocomplete"
                        @account-chosen="onConsultingForesterChosen"
                        :fetchTagsAndAccounts="false"
                        hide-refresh
                        :tabFilter="false"
                        :hint="requirement(TractRequiredField.ConsultingForesterAccount).hint"
                        :required="requirement(TractRequiredField.ConsultingForesterAccount).required"
                      />
                    </LoadableComponent>
                  </v-col>
                  <v-col cols="12" class="mt-n2">
                    <LoadableComponent :loading="loading">
                      <TeamAutocomplete
                        :propTeamId="tractGroup.teamId"
                        @team-chosen="onTeamChosen"
                        :hint="requirement(TractRequiredField.Team).hint"
                        :rules="requirement(TractRequiredField.Team).rules"
                      />
                    </LoadableComponent>
                  </v-col>
                  <v-col cols="12" class="mt-n2">
                    <LoadableComponent :loading="loading">
                      <UserAutocomplete
                        :label="$t('forester')"
                        :propUserId="tractGroup.foresterUserId"
                        elementId="tract-group-forester"
                        @user-chosen="onForesterChosen"
                        :propTeam="team"
                        :fetchUsers="false"
                        :hint="requirement(TractRequiredField.Forester).hint"
                        :rules="requirement(TractRequiredField.Forester).rules"
                      />
                    </LoadableComponent>
                  </v-col>
                  <v-col cols="7" class="mt-n2">
                    <LoadableComponent :loading="loading">
                      <EntitySelector
                      :entities="entities"
                      :initialEntity.sync="selectedEntity"
                      @entity-selected="selectedEntity = $event"
                      :hint="$t('required')"
                      persistentHint
                      :label="$t('purchaser')"
                      :disabled="purchaserDisabled"
                      selectStyle=""/>
                    </LoadableComponent>
                  </v-col>
                  <v-col cols="5" class="mt-n2">
                    <LoadableComponent :loading="loading">
                      <MoneyTextField
                      :initialValue="tractGroup.cost"
                      :label="$t('obligation')"
                      :disabled="!costMutable"
                      @val-changed="tractGroup.cost = $event"
                      />
                    </LoadableComponent>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="6">
            <v-card outlined class="fill-height">
              <v-card-text class="pt-0">
                <v-row dense>
                  <v-col cols="12" v-if="isObligationType">
                    <LoadableComponent :loading="loading">
                      <v-row dense class="mt-4">
                        <v-col>
                          <span class="subtitle-1" style="color: rgba(0, 0, 0, 0.6)">
                            {{ $t('obligationRate') }}
                          </span>
                          <Icon
                          icon="mdi-pencil"
                          :small="false"
                          :tooltipText="$t('editObligationRate')"
                          @icon-clicked="editObligationNamedRate"/>
                        </v-col>
                      </v-row>
                      <v-row dense class="mt-n2">
                        <v-col cols="12">
                          <v-card v-if="obligationRate" class="mt-1">
                            <v-card-text>
                              <v-row no-gutters dense>
                                <v-col cols="6" sm="5">
                                  {{ $t('name') }}:
                                </v-col>
                                <v-col cols="6" sm="7" class="text-right">
                                  {{ obligationRate?.name ?? $t('notSet') }}
                                </v-col>
                              </v-row>
                              <v-row no-gutters dense class="mt-2">
                                <v-col cols="6" sm="5">
                                  {{ $t('payBy') }}:
                                </v-col>
                                <v-col cols="6" sm="7" class="text-right">
                                  <span class="mr-2">{{ PayBy.fromInt(obligationRate?.costCalculation?.payBy) }}</span>
                                  <Icon
                                    v-if="obligationRate?.costCalculation?.payBy === PayBy.Volume.value && obligationRate?.costCalculation?.rateSheet"
                                    icon="mdi-note-text"
                                    :tooltipText="`${$t('rateSheet')}: ${obligationRate?.costCalculation?.rateSheet?.name ?? obligationRate?.costCalculation?.rateSheet}`"
                                    @icon-clicked="showRateSheetDialog"/>
                                </v-col>
                              </v-row>
                              <v-row no-gutters dense class="mt-2">
                                <v-col cols="6" sm="5">
                                  {{ $t('payOn') }}:
                                </v-col>
                                <v-col cols="6" sm="7" class="text-right">
                                  {{ PayOn.fromInt(obligationRate?.costCalculation?.payOn) }}
                                </v-col>
                              </v-row>
                            </v-card-text>
                          </v-card>
                          <span v-else class="subtitle-1">
                            {{ $t('notSet') }}
                          </span>
                        </v-col>
                      </v-row>
                    </LoadableComponent>
                  </v-col>
                  <v-col cols="12" class="mb-n2">
                    <LoadableComponent :loading="loading">
                      <v-checkbox
                        v-model="tractGroup.requiresExt1"
                        :label="$t('requiresExt1')"
                        hide-details
                      />
                    </LoadableComponent>
                  </v-col>
                  <v-col cols="12" class="mb-n2">
                    <LoadableComponent :loading="loading">
                      <v-checkbox
                        v-model="tractGroup.requiresExt2"
                        :label="$t('requiresExt2')"
                        hide-details
                      />
                    </LoadableComponent>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <TractLandowners
              :showOwnership="false"
              :propLandowners="tractGroup.landowners"
              :loading="loading"
              :hint="requirement(TractRequiredField.Landowners).hint"
              @ownership-changed="onLandownersChanged"
            />
          </v-col>
        </v-row>
        </v-container>
        <template #left-action v-if="tractGroup.landowners?.length > 0">
          <v-row class="px-3">
            <v-col cols="12">
              {{ $t('allocatedOwnership', { x: formatPercentage(allocatedOwnership) }) }}
            </v-col>
          </v-row>
        </template>
      </FormWrapper>
    </v-card-text>
    <Dialog :stateId="namedRateDialogId" @close="closeNamedRateDialog">
      <NamedRateForm
      v-if="editNamedRates"
      @close="closeNamedRateDialog"
      @named-rate-changed="onNamedRateChanged"
      :propNamedRate="obligationRate"
      :tractGroup="tractGroup"
      :deferSave="!tractGroupId"/>
    </Dialog>
    <Dialog :stateId="rateSheetDialogId" @close="closeRateSheetDialog">
      <RateSheetViewer
      v-if="viewingRateSheetDetails"
      :accountId="purchasingAccountId"
      :rateSheet="viewingRateSheetDetails"/>
    </Dialog>
  </v-card>
</template>

<script>
import fieldRules from '@/utils/rules'
import { mapActions, mapGetters } from 'vuex'
import { formatPercentage } from '@/utils/NumericMutations'
import { TractTypeSpecialization, NamedRateSpecialization, PayBy, PayOn, TractRequiredField } from '@/utils/Enumerations.js'
import { requirementAnnotation, missingGroupRequiredFields, requiredFieldMessages } from '@/utils/TractRequirements.js'
import { uniqueDialogId } from '@/utils/componentHelpers'

export default {
  name: 'TractGroupDialog',

  props: {
    tractGroupId: {
      type: Number,
      default: null
    }
  },
  components: {
    TractTypeSelector: () => import('@/components/tract/TractTypeSelector.vue'),
    AccountAutocomplete: () => import('@/components/autocomplete/AccountAutocomplete.vue'),
    TeamAutocomplete: () => import('@/components/autocomplete/TeamAutocomplete.vue'),
    UserAutocomplete: () => import('@/components/autocomplete/UserAutocomplete.vue'),
    TractLandowners: () => import('@/components/tract/tract-form/TractLandowners.vue'),
    FormWrapper: () => import('@/components/core/FormWrapper.vue'),
    BaseDialogActions: () => import('@/components/core/BaseDialogActions.vue'),
    EntitySelector: () => import('@/components/accounting/EntitySelector.vue'),
    MoneyTextField: () => import('@/components/helper/MoneyTextField.vue'),
    LoadableComponent: () => import('@/components/helper/LoadableComponent.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    Dialog: () => import('@/components/Dialog.vue'),
    NamedRateForm: () => import('@/components/tract/cruise-data/NamedRateForm.vue'),
    RateSheetViewer: () => import('@/components/activity/rate-sheet/RateSheetViewer.vue')
  },
  data: () => ({
    PayBy,
    PayOn,
    TractRequiredField,
    rules: fieldRules.rules,
    loading: true,
    team: undefined,
    entities: [],
    selectedEntity: undefined,
    selectedTractType: undefined,
    namedRates: [],
    editNamedRates: false,
    viewingRateSheetDetails: undefined,
    namedRateDialogId: uniqueDialogId('named-rate-dialog'),
    rateSheetDialogId: uniqueDialogId('rate-sheet-detail-dialog'),
    tractGroup: {
      cost: 0,
      name: '',
      code: '',
      tractTypeId: null,
      consultingForesterAccountId: null,
      teamId: null,
      foresterUserId: null,
      landowners: [],
      requiresExt1: false,
      requiresExt2: false
    }
  }),
  async created () {
    const [entities] = await Promise.all([
      this.fetchAllBusinessEntities(),
      this.fetchAccounts(),
      this.fetchAccountTags(),
      this.getAllUsers(),
      this.fetchTeams()
    ])
    this.entities = entities

    if (this.entities.length === 1) {
      this.selectedEntity = this.entities[0]
    }

    if (this.tractGroupId) {
      await this.initializeTractGroup()
    }

    setTimeout(_ => { this.loading = false }, 125) // Smooth out loading
  },
  watch: {
    selectedEntity: {
      deep: true,
      immediate: true,
      handler (val) {
        this.tractGroup.businessEntityId = val?.businessEntityId
      }
    },
    selectedTractType: {
      deep: true,
      immediate: true,
      handler (val) {
        this.tractGroup.tractTypeId = val?.tractTypeId
      }
    }
  },
  computed: {
    ...mapGetters('tract-type', ['allTractTypes']),
    ...mapGetters('user', ['businessEntities']),
    isFormValid () {
      return this.tractGroup.name && this.tractGroup.tractTypeId && this.missingGroupRequirements.length === 0
    },

    groupTractType () {
      return this.allTractTypes.find(tt => tt.tractTypeId === this.tractGroup.tractTypeId)
    },

    missingGroupRequirements () {
      return missingGroupRequiredFields(this.tractGroup, this.groupTractType)
    },

    allocatedOwnership () {
      return Number(Number(this.tractGroup.landowners.reduce((total, owner) => total + (owner.accountId ? owner.ownership : 0), 0)).toFixed(3))
    },

    costMutable () {
      return this.selectedTractType?.specialization === TractTypeSpecialization.ObligationCapped.value
    },

    isObligationType () {
      return true
      // TODO: IH - Address with more tract group / named rates
      // return this.selectedTractType?.specialization === TractTypeSpecialization.ObligationCapped.value ||
      //        this.selectedTractType?.specialization === TractTypeSpecialization.ObligationUncapped.value
    },

    obligationRate () {
      return this.namedRates.find(nr => nr.specialization === NamedRateSpecialization.Obligation.value)
    },

    purchasingAccountId () {
      return this.businessEntities.find(be => be.businessEntityId === this.tractGroup.businessEntityId)?.accountId
    },

    purchaserDisabled () {
      return this.obligationRate != null
    }
  },
  mounted () {
    this.$nextTick(() => {
      if (this.$refs.nameField) {
        this.$refs.nameField.focus()
      }
    })
  },
  methods: {
    formatPercentage,
    requiredFieldMessages,
    requirement (field) {
      return requirementAnnotation(this.groupTractType, field)
    },
    ...mapActions('snackbar', ['setSnack', 'setSnackError']),
    ...mapActions('tract', ['createTractGroup', 'updateTractGroup', 'fetchTractGroup', 'fetchNamedRates']),
    ...mapActions('account', ['fetchAccounts']),
    ...mapActions('tags', ['fetchAccountTags']),
    ...mapActions('team', ['fetchTeams']),
    ...mapActions('user', ['fetchAllBusinessEntities', 'getAllUsers']),
    ...mapActions('tract-type', ['fetchTractTypes']),
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),
    ...mapActions('rate-sheet', ['fetchRateSheet']),
    onTractTypeChosen (type) {
      this.selectedTractType = type
    },
    onConsultingForesterChosen (account) {
      this.tractGroup.consultingForesterAccountId = account?.accountId ?? null
    },
    onTeamChosen (team) {
      this.team = team
      this.tractGroup.teamId = team?.teamId ?? null
    },
    onForesterChosen (user) {
      this.tractGroup.foresterUserId = user?.applicationUserId ?? null
    },
    onLandownersChanged (landowners) {
      this.tractGroup.landowners = landowners
    },
    async initializeTractGroup () {
      this.tractGroup = await this.fetchTractGroup(this.tractGroupId)

      if (this.tractGroup.businessEntityId) {
        this.selectedEntity = this.entities.find(e => e.businessEntityId === this.tractGroup.businessEntityId)
      }

      this.namedRates = await this.fetchNamedRates(this.tractGroupId)
    },

    async save () {
      if (!this.tractGroup.name || !this.tractGroup.tractTypeId) {
        this.setSnackError(this.$t('missingRequiredFields'))
        return
      }
      if (this.costMutable && (!this.tractGroup.cost || this.tractGroup.cost <= 0)) {
        this.setSnackError(this.$t('obligationMustBeGreaterThanZero'))
        return
      }
      if (this.tractGroupId) {
        const result = await this.updateTractGroup(this.tractGroup)
        this.$emit('tract-group-updated', result)
      } else {
        const payload = { ...this.tractGroup }
        if (this.namedRates.length > 0) {
          payload.namedRates = this.namedRates
        }
        const result = await this.createTractGroup(payload)
        this.$emit('tract-group-created', result)
      }
    },
    obligationTractType (tractType) {
      return tractType.specialization === TractTypeSpecialization.ObligationUncapped.value || tractType.specialization === TractTypeSpecialization.ObligationCapped.value
    },

    async editObligationNamedRate () {
      if (this.selectedEntity == null) {
        this.setSnackError(this.$t('selectPurchaserBeforeEditingRate'))
        return
      }
      if (this.selectedEntity.accountId == null) {
        this.setSnackError(this.$t('purchaserAccountMustBeSet'))
        return
      }
      this.editNamedRates = true
      if (this.obligationRate != null) {
        const namedRate = this.namedRates.find(nr => nr.specialization === NamedRateSpecialization.Obligation.value)
        if (namedRate?.namedRateId != null && namedRate.costCalculation?.rateSheet != null) {
          namedRate.costCalculation.rateSheet = await this.fetchRateSheet(namedRate.costCalculation.rateSheetId)
        }
      }
      this.openOrUpdateDialog({ id: this.namedRateDialogId, width: '40vw' })
    },

    async showRateSheetDialog () {
      this.viewingRateSheetDetails = this.obligationRate?.costCalculation?.rateSheetId ? await this.fetchRateSheet(this.obligationRate?.costCalculation?.rateSheetId) : this.obligationRate?.costCalculation?.rateSheet
      this.openOrUpdateDialog({ id: this.rateSheetDialogId, width: '85vw' })
    },

    closeRateSheetDialog () {
      this.viewingRateSheetDetails = undefined
      this.closeDialogsAtOrAbove(this.rateSheetDialogId)
    },

    async closeNamedRateDialog (refresh = false) {
      this.editNamedRates = false
      this.closeDialogsAtOrAbove(this.namedRateDialogId)
      if (!refresh || !this.tractGroupId) return
      this.namedRates = await this.fetchNamedRates(this.tractGroup.tractGroupId)
    },

    onNamedRateChanged (namedRate) {
      const index = this.namedRates.findIndex(nr => nr.specialization === namedRate.specialization)
      if (index >= 0) {
        this.$set(this.namedRates, index, namedRate)
      } else {
        this.namedRates.push(namedRate)
      }
    }
  }
}
</script>
